/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';
import { CmsImage } from '@hyphengroup/cms-components/components/CmsImage';
import { FeCarousel } from '@hyphengroup/cms-components/components/FeCarousel';
import classNames from 'classnames';
import { useBreakpoint } from 'hooks/useBreakpoint';
import React from 'react';
import { TRANSLATIONS } from '../translations';
import {
  ComponentTestimonials,
  EnumTestimonialsBackgroundColor,
} from '$/(cms)/_modules/cmsService/types';
import { FeLink } from '$/components/FeLink';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';

export const HPReviewCards: React.FC<ComponentTestimonials> = ({
  backgroundColor,
  title,
  items,
}) => {
  const context = useClientContext();
  const staticText = getClientTranslations(TRANSLATIONS, context);
  const isNotDesktop = useBreakpoint({
    _: true,
    xxl: false,
  });
  const shouldAutoPlay = items?.length && (isNotDesktop || items.length > 3);

  return (
    <div
      className={classNames({
        'bg-colors/neutral-white':
          backgroundColor === EnumTestimonialsBackgroundColor.White,
        'bg-colors/brand-default-secondary-light':
          backgroundColor === EnumTestimonialsBackgroundColor.LightGreen,
        'bg-colors/brand-default-primary-50':
          backgroundColor === EnumTestimonialsBackgroundColor.LightBlue,
        'bg-colors/neutral-gray-100':
          backgroundColor === EnumTestimonialsBackgroundColor.LightGrey,
        // legacy gradient
        'bg-gradient-to-l from-fill/base to-[#ECFFEF]': !backgroundColor,
      })}
    >
      <div className="mx-auto max-w-screen-2xl px-4 py-8 md:px-6 lg:px-8 lg:py-10 xl:px-9 xl:py-11">
        <h3 className="font-bold text-gray-900 text-2xl leading-[30px] md:mb-6 md:text-center md:text-3xl md:leading-[34px] lg:mb-7 lg:text-4xl lg:leading-[44px] xl:mb-8">
          {title}
        </h3>

        <FeCarousel
          swiperPropsOverride={{
            pagination: true,
            centeredSlides: true,
            slidesPerView: 'auto',
            spaceBetween: 16,
            autoplay: shouldAutoPlay
              ? {
                  delay: 2000,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }
              : false,
            breakpoints: {
              1280: {
                centeredSlides: false,
                slidesPerView: 3,
                spaceBetween: 36,
                autoplay:
                  items && items.length > 3
                    ? {
                        delay: 2000,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true,
                      }
                    : false,
              },
            },
          }}
          slideClassNames="flex flex-col py-padding/y-sm !h-auto"
          navigationWrapperClassNames="hidden 1280:block"
          swiperNestedClassNames="!pb-4 overflow-visible"
        >
          {(items ?? [])?.map((item) => (
            <div key={item.title} className="flex justify-center">
              <div className="flex min-h-60 w-full max-w-screen-2sm flex-col rounded-radius/card border border-gray-300 p-4 bg-white md:p-6 lg:min-h-80 xl:min-h-[310px] ">
                <h3 className="mb-2 font-bold text-gray-900 text-lg md:text-xl md:leading-7 lg:text-2xl lg:leading-[30px]">
                  {item?.title}
                </h3>
                <p className="grow font-medium text-gray-700 text-sm md:text-base md:leading-[22px] lg:text-lg lg:leading-6">
                  {item?.description}
                </p>

                <div className="mt-4 flex items-center ">
                  {item?.author_image && (
                    <CmsImage
                      unoptimized
                      width={40}
                      height={40}
                      src={item?.author_image?.url}
                      alt={item?.author_image?.alternativeText}
                      className="mr-2 rounded-full"
                    />
                  )}
                  <div>
                    <p className="font-semibold text-gray-900 text-xs md:text-sm md:leading-5 lg:text-base lg:leading-[22px]">
                      {item?.author_name}
                    </p>
                    {item?.review_url && (
                      <p className="font-normal text-gray-700 text-xs md:text-sm md:leading-5 lg:text-base lg:leading-[22px]">
                        {staticText.reviewed_on}{' '}
                        <FeLink
                          target="_blank"
                          href={item?.review_url}
                          className="underline"
                        >
                          {item?.review_label}
                        </FeLink>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </FeCarousel>
      </div>
    </div>
  );
};
